import { h, defineComponent, ref, watch } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, indexView, loader, pagination } from '@/ui/components';
import { bulkDelete, list as getCategoryList, remove } from '@/services/api/resources/category';
import { emptyList } from '@/services/api';
import { CategoryList, CategoryRecord } from '@/entities/category';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import { bulkDeleteButton } from '@/ui/components/Button';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import * as qs from '@/ui/queryState';
import { searchFilter } from '@/ui/components/filters/filters-horizontal';
import { filterFormHorizontal } from '@/ui/components/Columns';
import { SortDirection } from '@/ui/types';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { useCategoryCheck } from '@/ui/pages/category/useCategoryCheck';
import CategoryEmpty from '@/ui/pages/category/CategoryEmpty.vue';
import { BulkDeletableTable } from '@/ui/components/table/BulkDeletableTable';
import { useBulkDeletable } from '@/ui/pages/useBulkDeletable';

export const CategoryIndexPage = defineComponent({
  name: 'CategoryIndexPage',
  setup() {
    // Category is part of the 'analytics' feature
    if (!hasFeature('analytics')) {
      return renderResourceTeaserPage(
        t('common.categories'),
        t('category.teaser'),
        t('common.teaser'),
        'categoryIndex',
      );
    }

    if (!hasPermission(Permissions.MANAGE_CATEGORIES)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);
    const search = qs.string('search', '');

    // state stored in query string
    const { page, perPage } = usePagination();
    const { sort, dir } = useSorting('createdAt', SortDirection.Descending);
    const { checkResult, performCheck } = useCategoryCheck();

    // get list task
    const list = ref<CategoryList>(emptyList());
    const getList = useTask(
      () => getCategoryList({
        page: page.value,
        perPage: perPage.value,
        search: search.value,
        sort: sort.value,
        dir: dir.value,
      }),
      listResponse => {
        list.value = listResponse;
        initialLoading.value = false;
      },
    );
    watch([page, perPage, search], getList.run);

    const { selectedResources, deleteBulkTask, onCheckedRowsUpdate, isBulkDeletionDisabled } = useBulkDeletable({
      getList,
      bulkDeleteFn: bulkDelete,
    });

    return function render() {
      return loader('div', performCheck, [
        checkResult.value ? indexView(
          [
            filterFormHorizontal([
              bulkDeleteButton({ resources: selectedResources, disabled: isBulkDeletionDisabled }),
              searchFilter(search),
            ]),
          ],
          [
            box([
              loader('div', getList, [
                h(BulkDeletableTable, {
                  props: {
                    editRoute: 'categoryEdit',
                    list: list.value.data,
                    deleteFn: remove,
                    selectedRecords: selectedResources.value,
                    loading: deleteBulkTask.state.value.pending,
                    paginationLinks: list.value.links,
                    isRowCheckable: (row: BaseEntity) => row.authorizedTo.delete,
                    recordName: {
                      plural: t('common.categories'),
                      singular: t('common.category'),
                    },
                  },
                  on: {
                    'update:checkedRows': onCheckedRowsUpdate,
                  },
                  scopedSlots: { title: ({ row: user }: { row: CategoryRecord }) => user.data.title },
                }),
                pagination({
                  current: page.value,
                  total: list.value.meta.total,
                  perPage: perPage.value,
                }),
              ], !initialLoading.value),
            ]),
          ],
        ) : box([h(CategoryEmpty)]),
      ], false);
    };
  },
});
